<template>
  <div class="analyze-indoor-building-selector">
    <b-form-select
      v-bind:id="elementId"
      v-bind:value="activeIndoorBuildingId"
      v-bind:options="options"
      v-bind:size="size"
      v-on:input="$_onSelectionChanged"
    ></b-form-select>
  </div>
</template>

<script>
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import { mapState } from 'vuex'

/**
 * ビル選択コンポーネント
 */
export default {
  name: 'AppAnalyzeIndoorBuildingSelector',
  props: {
    /**
     * サイズ
     * @values sm, md, lg
     */
    size: {
      type: String,
      default: 'sm',
    },
    /**
     * IDのプレフィックス
     */
    idPrefix: {
      type: String,
      default: '',
    },
    /**
     * ユーザー選択値
     */
    users: {
      type: Array,
      default: null,
    },
    /**
     * 選択中建物ID
     */
    activeIndoorBuildingId: {
      type: Number,
      default: null,
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  computed: {
    ...mapState('indoorSite', ['indoorSites']),
    ...mapState('indoorBuilding', ['indoorBuildings']),
    /**
     * エレメントID
     * @type {String}
     */
    elementId: function () {
      return `${this.idPrefix}-indoor-building-selector`
    },
    /**
     * 選択ユーザーID(展開)
     * @type {Set.<number>}
     */
    userIdSet: function () {
      if (!this.users) return null
      return new Set(
        this.users.flatMap((d) => {
          if (d.type === 'user') {
            return [d.id]
          } else if (d.type === 'organization') {
            return d.users?.map((u) => u.id) || []
          } else {
            return []
          }
        })
      )
    },
    /**
     * 現場に紐付いていない建物
     * @type {Array.<Object>}
     */
    indoorBuildingsNotInSite: function () {
      return this.indoorBuildings.filter(
        (building) => !building.indoor_sites || !building.indoor_sites.length
      )
    },
    /**
     * 各アイテム用データ
     * @type {Array.<Object>}
     */
    options: function () {
      const buildingsWithSites = this.indoorSites
        .filter(
          (site) =>
            !this.userIdSet ||
            site.extend_indoor_site_user_relations.some((u) =>
              this.userIdSet.has(u.user_id)
            )
        )
        .reduce((acc, val) => {
          return acc.concat(
            val.indoor_site_indoor_building_relations.map((building) => ({
              value: building.id,
              text: `${val.site_name}(${building.name})`,
            }))
          )
        }, [])
      const buildings = this.indoorBuildingsNotInSite.map((building) => ({
        value: building.id,
        text: building.name,
      }))
      const defaultLabel = {
        value: null,
        text: this.$t('appAnalyzeIndoorBuildingSelector.noSelection'),
      }

      return [defaultLabel, ...buildingsWithSites, ...buildings]
    },
  },
  created: function () {
    this.$_getIndoorSites()
    this.$_getIndoorBuildings()
  },
  mounted: function () {
    this.$log.info('AppAnalyeIndoorBuildingSelector mounted!')
  },
  methods: {
    /**
     * 現場データ取得
     */
    $_getIndoorSites: async function () {
      try {
        await this.$store.dispatch('indoorSite/updateIndoorSitesWithThrottle')
      } catch (error) {
        this.handleApiError(error, () =>
          this.$log.info('failed to fetch indoor sites. use fetched before.')
        )
      }
    },

    /**
     * 建物データ取得
     */
    $_getIndoorBuildings: async function () {
      try {
        await this.$store.dispatch(
          'indoorBuilding/updateIndoorBuildingsWithThrottle'
        )
      } catch (error) {
        this.handleApiError(error, () =>
          this.$log.info(
            'failed to fetch indoor buildings. use fetched before.'
          )
        )
      }
    },
    /**
     * 選択変更時処理
     * @param {Object} id 選択されたID
     */
    $_onSelectionChanged: function (id) {
      /**
       * 建物選択イベント
       * @param {Object} id 選択されているビルID
       */
      this.$emit('indoor-building-selected', id)
    },
  },
}
</script>

<style lang="scss" scoped>
.analyze-indoor-building-selector {
  width: 100%;
}
</style>
